// ===== WebSocket 스트리밍 핸들러 (위임 에이전트 + 그룹채팅) =====
// createWsHandlers()가 스프레드로 합성하는 핸들러 묶음. classic script로 로드되며
// ws-handlers.js보다 먼저 실려야 한다(index.html 순서).

function createWsStreamHandlers() {
  const streams = agentStreaming as Record<string, WsStreamState>;
  const controllers = agentControllers as Record<string, AvatarController>;
  return {
    // ===== Delegation Handler =====
    onDelegation(data: WsPayload) {
      hideTyping();
      setWaiting(true);
      hana.stopTalking(); // talking 중이었으면 graceful 종료, 아니면 no-op
      addDelegationMessage(data.content);
    },

    // ===== Agent Message Handlers =====
    onAgentStart(data: WsPayload) {
      hideTyping();
      showAgentCard(data.agent);

      const profile = wsAgentProfile(data.agent);
      const name = profile?.name || data.agentName;
      const particle = hasJongseong(name) ? "이가" : "가";
      const statusText = data.typingStatus
        ? `${name}${particle} ${data.typingStatus}`
        : null;
      showAgentTyping(data.agent, statusText ?? undefined);
      delete streams[data.agent];
    },

    onAgentStream(data: WsPayload) {
      hideTyping();
      const id = data.agent;
      if (!streams[id]) {
        streams[id] = { bubble: requireWsElement(startAgentStreamingMessage(id, { sessionId: resolveAnchorSessionId(data) }), "agent stream bubble"), text: "", thinking: "", thinkingEl: null };
      }
      const s = streams[id];
      const ctrl = controllers[id];
      if (ctrl && !ctrl.isTalking) ctrl.startTalking();

      // 폴백: thinking 블록이 열려 있으면 첫 stream 도착 시 자동 접힘
      if (s.thinkingEl && s.thinkingEl.open) {
        s.thinkingEl.open = false;
      }

      s.text += data.delta;
      s.bubble.innerHTML = renderStreamingMarkdown(s.text);
      wsChatMessagesElement.scrollTop = wsChatMessagesElement.scrollHeight;

      if (data.expression) {
        const avatarImg = s.bubble.closest(".message")?.querySelector<HTMLImageElement>(".message-avatar");
        if (avatarImg) {
          const newSrc = getAgentAvatarPath(id, data.expression);
          avatarImg.src = newSrc;
          avatarImg.onerror = function() { this.src = getAvatarPath(data.expression); };
        }
      }
    },

    onAgentThinking(data: WsPayload) {
      const id = data.agent;
      const s = streams[id];
      if (!s) return;
      s.thinking += data.delta;
      ensureThinkingBlock(s);
      const thinkingContent = s.thinkingEl?.querySelector<HTMLElement>(".thinking-content");
      if (thinkingContent) thinkingContent.textContent = s.thinking;
    },

    onAgentThinkingDone(data: WsPayload) {
      const id = data.agent;
      const s = streams[id];
      if (!s || !s.thinkingEl) return;
      s.thinkingEl.open = false;
    },

    onAgentComplete(data: WsPayload) {
      hideTyping();
      // setWaiting(false) 제거: 체인 라운드 중 cancel 버튼이 사라지지 않도록
      // 전체 체인 완료는 onGroupChatDone에서만 setWaiting(false) 호출
      const id = data.agent;
      const ctrl = controllers[id];
      if (ctrl) ctrl.stopTalking();

      const s = streams[id];
      if (s) {
        const finalText = data.content || s.text;
        s.bubble.innerHTML = renderMarkdown(finalText);
        anchorStreamedMessage(s.bubble, resolveAnchorSessionId(data), data.logSeq);

        if (data.expression) {
          const avatarImg = s.bubble.closest(".message")?.querySelector<HTMLImageElement>(".message-avatar");
          if (avatarImg) {
            const newSrc = getAgentAvatarPath(id, data.expression);
            avatarImg.src = newSrc;
            avatarImg.onerror = function() { this.src = getAvatarPath(data.expression); };
          }
        }

        // thinking 최종 확정
        if (data.thinking) {
          ensureThinkingBlock(s);
          const thinkingContent = s.thinkingEl?.querySelector<HTMLElement>(".thinking-content");
          if (thinkingContent) thinkingContent.textContent = data.thinking;
        }

        delete streams[id];
      } else {
        addAgentMessage(id, data.content || "", {
          expression: data.expression,
          seq: data.logSeq,
          sessionId: resolveAnchorSessionId(data),
        });
      }

      if (typeof data.notificationSourceEventKey === "string") consumeCanonicalNotification(data);
    },

    onAgentEnd(data: WsPayload) {
      // 에이전트 퇴장
      if (data.agent) {
        hideAgentCard(data.agent);
      } else {
        // fallback: 모든 활성 에이전트 퇴장
        for (const id of Object.keys(controllers)) {
          hideAgentCard(id);
        }
      }

      if (data.returnSpeech) {
        addDelegationMessage(data.returnSpeech);
      }
      // 하나는 독립적으로 드리프트 유지 — 강제 표정 변경 없음
    },

    onCancelled() {
      hideTyping();
      setWaiting(false);
      hana.stopTalking();

      // 스트리밍 중이었으면 현재까지 내용 확정
      if (streamingBubble) {
        streamingBubble.innerHTML = renderMarkdown(streamingText);
        streamingBubble = null;
        streamingText = "";
      }
      for (const id of Object.keys(streams)) {
        const s = streams[id];
        if (s.bubble) s.bubble.innerHTML = renderMarkdown(s.text);
      }
      for (const id of Object.keys(streams)) delete streams[id];
      for (const id of Object.keys(controllers)) {
        controllers[id]?.stopTalking();
      }
    },

    // ===== Group Chat Handlers =====
    // 화자가 기억을 읽고 프롬프트를 조립하는 동안의 표시. 이어지는 speaker-start가 같은 인디케이터를
    // "…생각하고 있어요"로 바꾼다(showAgentTyping은 기존 인디케이터를 지우고 새로 단다).
    onSpeakerMemoryLoading(data: WsPayload) {
      if (isOtherSessionEvent(data)) return;
      hideTyping();
      showAgentCell(data.speaker);

      const profile = wsAgentProfile(data.speaker);
      const name = profile?.name || data.speakerName;
      const particle = hasJongseong(name) ? "이가" : "가";
      showAgentTyping(data.speaker, `${name}${particle} 기억을 읽는 중이에요`);
    },

    onSpeakerStart(data: WsPayload) {
      // 다른 방을 보는 중이면 무렌더 (onRoomMessage 관례, P2 fan-out 이벤트가 타 세션으로 새는 것 방지)
      if (isOtherSessionEvent(data)) return;
      hideTyping();
      showAgentCell(data.speaker);
      highlightSpeaker(data.speaker);

      const profile = wsAgentProfile(data.speaker);
      const name = profile?.name || data.speakerName;
      const particle = hasJongseong(name) ? "이가" : "가";
      showAgentTyping(data.speaker, `${name}${particle} 생각하고 있어요`);
      delete streams[data.speaker];
    },

    onGroupChatStream(data: WsPayload) {
      if (isOtherSessionEvent(data)) return;
      hideTyping();
      const id = data.speaker;
      if (!streams[id]) {
        streams[id] = { bubble: requireWsElement(startAgentStreamingMessage(id, { sessionId: resolveAnchorSessionId(data) }), "group stream bubble"), text: "", thinking: "", thinkingEl: null };
      }
      const s = streams[id];
      const ctrl = controllers[id];
      if (ctrl && !ctrl.isTalking) ctrl.startTalking();

      // 폴백: thinking 블록이 열려 있으면 첫 stream 도착 시 자동 접힘
      if (s.thinkingEl && s.thinkingEl.open) {
        s.thinkingEl.open = false;
      }

      s.text += data.delta;
      s.bubble.innerHTML = renderStreamingMarkdown(s.text);
      wsChatMessagesElement.scrollTop = wsChatMessagesElement.scrollHeight;

      if (data.expression) {
        const avatarImg = s.bubble.closest(".message")?.querySelector<HTMLImageElement>(".message-avatar");
        if (avatarImg) {
          const newSrc = getAgentAvatarPath(id, data.expression);
          avatarImg.src = newSrc;
          avatarImg.onerror = function() { this.src = getAvatarPath(data.expression); };
        }
      }
    },

    onGroupChatThinking(data: WsPayload) {
      if (isOtherSessionEvent(data)) return;
      const id = data.speaker;
      const s = streams[id];
      if (!s) return;
      s.thinking += data.delta;
      ensureThinkingBlock(s);
      const thinkingContent = s.thinkingEl?.querySelector<HTMLElement>(".thinking-content");
      if (thinkingContent) thinkingContent.textContent = s.thinking;
    },

    onGroupChatThinkingDone(data: WsPayload) {
      const id = data.speaker;
      const s = streams[id];
      if (!s || !s.thinkingEl) return;
      s.thinkingEl.open = false;
    },

    onSpeakerEnd(data: WsPayload) {
      if (isOtherSessionEvent(data)) return;
      hideTyping();
      const id = data.speaker;
      unhighlightSpeaker(id);

      const ctrl = controllers[id];
      if (ctrl) ctrl.stopTalking();

      const s = streams[id];
      if (s) {
        const finalText = data.content || s.text;
        s.bubble.innerHTML = renderMarkdown(finalText);
        // Phase 3(bigtionax-memory-read-g-source-marker): 그룹채팅 화자 답변 스트리밍 분기 — 재작성·
        // 앵커 뒤 attach(§설계). onAgentComplete(direct-Hana)는 CAP21 범위 밖이라 그대로 둔다.
        anchorStreamedMessage(s.bubble, resolveAnchorSessionId(data), data.logSeq, data.memorySourceCount);

        if (data.expression) {
          const avatarImg = s.bubble.closest(".message")?.querySelector<HTMLImageElement>(".message-avatar");
          if (avatarImg) {
            const newSrc = getAgentAvatarPath(id, data.expression);
            avatarImg.src = newSrc;
            avatarImg.onerror = function() { this.src = getAvatarPath(data.expression); };
          }
        }

        // thinking 최종 확정
        if (data.thinking) {
          ensureThinkingBlock(s);
          const thinkingContent = s.thinkingEl?.querySelector<HTMLElement>(".thinking-content");
          if (thinkingContent) thinkingContent.textContent = data.thinking;
        }

        delete streams[id];
      } else if (data.content) {
        addAgentMessage(id, data.content, {
          expression: data.expression,
          seq: data.logSeq,
          sessionId: resolveAnchorSessionId(data),
          memorySourceCount: data.memorySourceCount,
        });
      }

      hideAgentCell(data.speaker);

      if (typeof data.notificationSourceEventKey === "string") consumeCanonicalNotification(data);
      notifyChatActivity();
    },

    onGroupChatDone(data: WsPayload) {
      if (isOtherSessionEvent(data)) return;
      hideTyping();
      // 원격(타 멤버) 실행이면 isWaiting/setWaiting 을 토글하지 않는다 — 내가 호출하지 않은
      // 실행 종료로 내 입력창이 잠겼다 풀리는 회귀 방지(load_bearing 참고).
      if (!isRemoteRoomEvent(data)) setWaiting(false);
      // 모든 에이전트 talking + 하이라이트 정리
      for (const id of Object.keys(controllers)) {
        controllers[id]?.stopTalking();
        unhighlightSpeaker(id);
      }
      for (const id of Object.keys(streams)) delete streams[id];
    },
  };
}
